import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { getLocaleLanguage } from 'modules/i18n';
import { ApiError } from 'utils/api';
import { CVBuilderFormValues } from 'utils/cv-form-db';
import { CV_BUILDER_STEPS, defaultFormValues } from '../cv-builder/constants';
import {
  CvBuilderData,
  CvBuilderDataContext
} from '../cv-builder/data/CvBuilderDataContext';
import { fromCvDto, toCreateCvRequest } from '../cv-builder/toCreateCvRequest';
import { submitGuestCv } from './api';
import { GuestSaveRequest } from './api-types';
import { isTerminalGrantError } from './grantErrors';
import { useGuestSession } from './GuestSessionContext';
import { hasEmptyPrefillableField, mergePrefill } from './prefill';
import { useGuestCvPrefill } from './query';
import { useGuestAutosave } from './useGuestAutosave';

// completedSteps cross the wire as backend slugs ('personal-details'); the wizard tracks
// them as CV_BUILDER_STEPS keys ('PERSONAL_DETAILS'). Translate at this transport boundary.
const SLUG_TO_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(CV_BUILDER_STEPS).map(([key, slug]) => [slug, key])
);

const keysToSlugs = (keys: string[] | undefined): string[] =>
  (keys ?? [])
    .map(key => CV_BUILDER_STEPS[key as keyof typeof CV_BUILDER_STEPS])
    .filter((slug): slug is string => Boolean(slug));

const slugsToKeys = (slugs: string[] | undefined): string[] =>
  (slugs ?? [])
    .map(slug => SLUG_TO_KEY[slug])
    .filter((key): key is string => Boolean(key));

const buildSaveRequest = (values: CVBuilderFormValues): GuestSaveRequest => ({
  cv: toCreateCvRequest(values),
  completedSteps: keysToSlugs(values.completedSteps)
});

/**
 * CvBuilderData implementation for an external guest: fully server-backed via /guest/curriculumVitaes,
 * no IndexedDB. Seeds from the redeem payload (resume), autosaves with single-flight +
 * debounce, and finalizes via POST /submit. A lapsed session (401) on any write transparently
 * re-redeems the still-valid link and retries; only a truly dead grant routes to the terminal
 * screen (there is no Auth0 session to recover).
 */
export const GuestCvDataProvider = ({ children }: { children: ReactNode }) => {
  const {
    sessionToken,
    redeemData,
    getSessionToken,
    recoverSession,
    markSubmitted,
    routeGrantError
  } = useGuestSession();
  const { scheduleSave, flushSave, lastSavedAt, saveFailed } = useGuestAutosave(
    sessionToken ?? '',
    recoverSession,
    routeGrantError
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Run a guest write, and if the session lapsed mid-flow (401), re-redeem the still-valid
  // link once and retry with the fresh token. A truly dead grant leaves recoverSession to
  // route to the terminal screen; this then re-throws the original session-expired error.
  const callWithRecovery = useCallback(
    async (run: (token: string) => Promise<void>): Promise<void> => {
      try {
        // getSessionToken() rather than the sessionToken render state: the flushSave gate
        // that runs just before this may have recovered the session, and the state from
        // the render this callback closed over is then one rotation behind — the stale
        // token would 401 and force yet another redeem.
        await run(getSessionToken() ?? '');
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.code === 'error.guest.session-expired'
        ) {
          const freshToken = await recoverSession();
          if (freshToken) {
            await run(freshToken);
            return;
          }
        }
        throw error;
      }
    },
    [getSessionToken, recoverSession]
  );

  // Resume values from the redeem payload (defaults + the saved CV, if any). Gated on
  // redeemData ALONE (set together with the initial session) and deliberately NOT on the
  // session-token value — so a mid-edit token refresh from recoverSession can never re-seed
  // and reset the in-progress form (the wizard's Formik uses enableReinitialize).
  const baseValues = useMemo<CVBuilderFormValues | null>(() => {
    if (!redeemData) {
      return null;
    }
    const cv = redeemData.cv ?? null;
    return {
      ...defaultFormValues,
      ...(cv ? fromCvDto(cv) : {}),
      completedSteps: slugsToKeys(redeemData.completedSteps),
      language: getLocaleLanguage() as CVBuilderFormValues['language']
    };
  }, [redeemData]);

  // Prefill when any personal field is empty — covers a brand-new guest AND a guest with a partial
  // CV (a CV row is created on first autosave with coerced defaults, so "a CV exists" is not a
  // reliable "has personal data" signal). Skip entirely once all personal fields are filled: no
  // fetch, no delay to a returning guest's resume. The merge itself is non-destructive regardless.
  const needsPrefill = baseValues
    ? hasEmptyPrefillableField(baseValues.personalDetails)
    : false;
  // Scope the prefill cache to the grant's link token (stable for the whole page load),
  // NOT the session token: a mid-edit session recovery rotates the session token, and a
  // rotating query key would reset isFetched, null out initialValues below, and remount
  // Formik over the guest's in-progress edits — wiping them.
  const [prefillScope] = useState(() =>
    new URLSearchParams(window.location.search).get('t')
  );
  const prefillQuery = useGuestCvPrefill(
    sessionToken ?? undefined,
    needsPrefill,
    prefillScope
  );

  const initialValues = useMemo<CVBuilderFormValues | null>(() => {
    if (!baseValues) {
      return null;
    }
    if (!needsPrefill) {
      return baseValues; // personal fields already complete → resume as-is
    }
    // Hold the form until the best-effort prefill settles, then seed only the empty personal
    // fields. A miss/failure resolves to null data → the form opens with the base values.
    if (!prefillQuery.isFetched) {
      return null;
    }
    return {
      ...baseValues,
      personalDetails: mergePrefill(
        baseValues.personalDetails,
        prefillQuery.data
      )
    };
  }, [baseValues, needsPrefill, prefillQuery.isFetched, prefillQuery.data]);

  // Last-saved snapshot for edit-mode discard (mirrors the authenticated IndexedDB
  // restore — keeps completedSteps, which a GET would not return).
  const lastSavedRef = useRef<CVBuilderFormValues | null>(null);
  useEffect(() => {
    if (initialValues && !lastSavedRef.current) {
      lastSavedRef.current = initialValues;
    }
  }, [initialValues]);

  // Synchronous guards (immune to the React state re-render gap):
  //  - submitInFlightRef: blocks a double-click from firing submit() twice.
  //  - hasSubmittedRef: once submit succeeds, autosave is frozen so a late saveDraft
  //    can't PUT against the consumed grant (401 → "expired" overwriting the success).
  const submitInFlightRef = useRef(false);
  const hasSubmittedRef = useRef(false);

  const value = useMemo<CvBuilderData>(
    () => ({
      initialValues,
      saveDraft: (values: CVBuilderFormValues) => {
        if (hasSubmittedRef.current) {
          return Promise.resolve(); // submitted → terminal, no more writes
        }
        lastSavedRef.current = values;
        scheduleSave(buildSaveRequest(values)); // fire-and-forget, never rejects
        return Promise.resolve();
      },
      lastSavedAt,
      saveFailed,
      loadDraft: () => Promise.resolve(lastSavedRef.current ?? undefined),
      clearDraft: () => Promise.resolve(), // server-owned: nothing local to clear
      submit: async (values: CVBuilderFormValues) => {
        if (submitInFlightRef.current || hasSubmittedRef.current) {
          return; // re-entrancy guard — synchronous, beats the disabled-button render gap
        }
        submitInFlightRef.current = true;
        setIsSubmitting(true);
        try {
          // final gate: cancel pending/in-flight autosaves and persist the latest CV
          // before the terminal submit, so we never submit against stale data.
          await flushSave(buildSaveRequest(values));
          await callWithRecovery(token => submitGuestCv(token));
          hasSubmittedRef.current = true; // freeze autosave before the terminal swap
          markSubmitted(); // terminal: swap to the submitted confirmation screen
        } catch (error) {
          submitInFlightRef.current = false; // allow a retry
          if (
            error instanceof ApiError &&
            error.code === 'error.guest.session-expired'
          ) {
            // recoverSession already routed to the matching terminal screen — nothing to add.
            return;
          }
          if (isTerminalGrantError(error)) {
            // Grant consumed elsewhere (second tab / an earlier attempt landed) or dead:
            // route to the terminal screen instead of a dead-end error toast. For
            // already-submitted that IS the success screen — the CV is in; freeze
            // autosave so no late write hits the consumed grant.
            if (error.code === 'error.access-grant.already-submitted') {
              hasSubmittedRef.current = true;
            }
            routeGrantError(error, 'submit');
            return;
          }
          throw error; // Summary surfaces a submit-error notification
        } finally {
          setIsSubmitting(false);
        }
      },
      isSubmitting,
      // The guest terminal is rendered at the page level (GuestStatusScreen, via
      // markSubmitted → redeemState='submitted'), NOT the port-driven in-wizard screen
      // — so the port flag stays false. Keeps the verified guest submit path untouched.
      submitted: false,
      // Guest landing/resume is driven by its own page logic (resumeStep), not the
      // Summary shortcut — keep the authenticated-only behavior out of the guest flow.
      hasExistingCv: false
    }),
    [
      initialValues,
      scheduleSave,
      flushSave,
      callWithRecovery,
      markSubmitted,
      routeGrantError,
      isSubmitting,
      lastSavedAt,
      saveFailed
    ]
  );

  return (
    <CvBuilderDataContext.Provider value={value}>
      {children}
    </CvBuilderDataContext.Provider>
  );
};
